/**
 * Analytics Service for HoneyDo Platform
 * 
 * Supports multiple analytics providers:
 * - Google Analytics 4 (GA4) for marketing analytics
 * - PostHog for product analytics (optional)
 * - Web Vitals for performance monitoring
 * 
 * Features:
 * - User behavior tracking
 * - Conversion tracking
 * - Revenue tracking
 * - Performance monitoring
 * - Error tracking integration
 */

import { appConfig, isProductionApp, isDevelopment } from '../config/app.config';

// Analytics providers configuration
interface AnalyticsConfig {
  ga4MeasurementId?: string;
  postHogApiKey?: string;
  postHogHost?: string;
  enableDebug: boolean;
}

// Event types for type safety
export interface AnalyticsEvent {
  event_name: string;
  event_category?: 'user_action' | 'system' | 'conversion' | 'error' | 'performance';
  event_label?: string;
  value?: number;
  user_id?: string;
  user_type?: string;
  family_id?: string;
  custom_parameters?: Record<string, any>;
}

// Conversion events specifically for SaaS metrics
export interface ConversionEvent {
  event_name: 'sign_up' | 'first_task' | 'subscription_start' | 'subscription_upgrade' | 'trial_start' | 'trial_convert';
  user_id: string;
  user_type?: string;
  family_id?: string;
  subscription_tier?: string;
  value?: number; // Revenue value in USD
  currency?: string;
}

// Performance metrics
export interface PerformanceEvent {
  event_name: 'api_response_time' | 'page_load_time' | 'core_web_vitals';
  endpoint?: string;
  duration_ms: number;
  status_code?: number;
  error_message?: string;
}

class AnalyticsService {
  private config: AnalyticsConfig;
  private isInitialized = false;
  private gtag?: (...args: any[]) => void;

  constructor() {
    this.config = {
      ga4MeasurementId: process.env.REACT_APP_GA4_MEASUREMENT_ID,
      postHogApiKey: process.env.REACT_APP_POSTHOG_API_KEY,
      postHogHost: process.env.REACT_APP_POSTHOG_HOST || 'https://app.posthog.com',
      enableDebug: isDevelopment()
    };

    this.init();
  }

  private async init() {
    try {
      // Initialize GA4 if available
      if (this.config.ga4MeasurementId && typeof window !== 'undefined') {
        this.gtag = (window as any).gtag;
        
        if (this.gtag) {
          // Configure GA4 with enhanced ecommerce and user properties
          this.gtag('config', this.config.ga4MeasurementId, {
            page_title: appConfig.appName,
            custom_map: {
              custom_parameter_1: 'user_type',
              custom_parameter_2: 'family_id'
            },
            debug_mode: this.config.enableDebug
          });
        }
      }

      // Initialize PostHog if configured
      if (this.config.postHogApiKey && typeof window !== 'undefined') {
        await this.initPostHog();
      }

      this.isInitialized = true;
      this.log('Analytics service initialized', { config: this.config });
    } catch (error) {
      console.error('Failed to initialize analytics:', error);
    }
  }

  private async initPostHog() {
    try {
      // Dynamically import PostHog to reduce bundle size
      const { default: posthog } = await import('posthog-js');
      
      posthog.init(this.config.postHogApiKey!, {
        api_host: this.config.postHogHost,
        debug: this.config.enableDebug,
        capture_pageview: false, // We'll handle this manually
        capture_pageleave: true,
        loaded: (posthog) => {
          // Set user properties based on environment
          posthog.register({
            app_version: process.env.REACT_APP_VERSION || '1.0.0',
            environment: appConfig.environment,
            app_mode: appConfig.defaultMode
          });
        }
      });

      (window as any).posthog = posthog;
    } catch (error) {
      console.warn('PostHog initialization failed:', error);
    }
  }

  private log(message: string, data?: any) {
    if (this.config.enableDebug) {
      console.log(`[Analytics] ${message}`, data);
    }
  }

  // Track page views
  trackPageView(page: string, title?: string) {
    if (!this.isInitialized) return;

    const pageData = {
      page_title: title || document.title,
      page_location: window.location.href,
      page: page
    };

    // GA4
    if (this.gtag) {
      this.gtag('config', this.config.ga4MeasurementId, pageData);
    }

    // PostHog
    if ((window as any).posthog) {
      (window as any).posthog.capture('$pageview', pageData);
    }

    this.log('Page view tracked', pageData);
  }

  // Track general events
  trackEvent(event: AnalyticsEvent) {
    if (!this.isInitialized) return;

    const eventData = {
      event_category: event.event_category,
      event_label: event.event_label,
      value: event.value,
      user_id: event.user_id,
      user_type: event.user_type,
      family_id: event.family_id,
      ...event.custom_parameters
    };

    // GA4
    if (this.gtag) {
      this.gtag('event', event.event_name, eventData);
    }

    // PostHog
    if ((window as any).posthog) {
      (window as any).posthog.capture(event.event_name, eventData);
    }

    this.log('Event tracked', { event: event.event_name, data: eventData });
  }

  // Track conversions (key for SaaS metrics)
  trackConversion(event: ConversionEvent) {
    if (!this.isInitialized) return;

    const conversionData = {
      event_category: 'conversion',
      currency: event.currency || 'USD',
      value: event.value || 0,
      user_id: event.user_id,
      user_type: event.user_type,
      family_id: event.family_id,
      subscription_tier: event.subscription_tier
    };

    // GA4 Enhanced Ecommerce
    if (this.gtag) {
      this.gtag('event', event.event_name, conversionData);
      
      // For subscription events, also track as purchase
      if (event.event_name === 'subscription_start' || event.event_name === 'subscription_upgrade') {
        this.gtag('event', 'purchase', {
          transaction_id: `${event.user_id}_${Date.now()}`,
          value: event.value,
          currency: event.currency || 'USD',
          items: [{
            item_id: event.subscription_tier || 'subscription',
            item_name: `HoneyDo ${event.subscription_tier || 'Subscription'}`,
            item_category: 'subscription',
            price: event.value,
            quantity: 1
          }]
        });
      }
    }

    // PostHog
    if ((window as any).posthog) {
      (window as any).posthog.capture(event.event_name, conversionData);
      
      // Set user properties for revenue tracking
      if (event.value && event.value > 0) {
        (window as any).posthog.people.increment('total_revenue', event.value);
        (window as any).posthog.people.set({
          subscription_tier: event.subscription_tier,
          last_subscription_event: event.event_name,
          last_subscription_date: new Date().toISOString()
        });
      }
    }

    this.log('Conversion tracked', { event: event.event_name, data: conversionData });
  }

  // Track performance metrics
  trackPerformance(event: PerformanceEvent) {
    if (!this.isInitialized) return;

    const perfData = {
      event_category: 'performance',
      value: event.duration_ms,
      endpoint: event.endpoint,
      status_code: event.status_code,
      error_message: event.error_message
    };

    // GA4
    if (this.gtag) {
      this.gtag('event', event.event_name, perfData);
    }

    // PostHog
    if ((window as any).posthog) {
      (window as any).posthog.capture(event.event_name, perfData);
    }

    this.log('Performance tracked', { event: event.event_name, data: perfData });
  }

  // Track errors
  trackError(error: Error, context?: string, userId?: string) {
    if (!this.isInitialized) return;

    const errorData = {
      event_category: 'error',
      event_label: context || 'javascript_error',
      error_message: error.message,
      error_stack: error.stack,
      user_id: userId,
      page_location: window.location.href
    };

    // GA4
    if (this.gtag) {
      this.gtag('event', 'exception', {
        description: error.message,
        fatal: false,
        ...errorData
      });
    }

    // PostHog
    if ((window as any).posthog) {
      (window as any).posthog.capture('error', errorData);
    }

    this.log('Error tracked', { error: error.message, data: errorData });
  }

  // Set user properties
  setUser(userId: string, properties: Record<string, any> = {}) {
    if (!this.isInitialized) return;

    // GA4
    if (this.gtag) {
      this.gtag('config', this.config.ga4MeasurementId, {
        user_id: userId,
        custom_parameters: properties
      });
    }

    // PostHog
    if ((window as any).posthog) {
      (window as any).posthog.identify(userId, properties);
    }

    this.log('User identified', { userId, properties });
  }

  // Revenue tracking helper
  trackRevenue(amount: number, currency = 'USD', userId?: string, subscriptionTier?: string) {
    this.trackConversion({
      event_name: 'subscription_start',
      user_id: userId || 'anonymous',
      value: amount,
      currency,
      subscription_tier: subscriptionTier
    });
  }

  // User journey tracking
  trackUserJourney(step: 'registration' | 'first_login' | 'first_task' | 'family_invite' | 'subscription_view' | 'subscription_start', userId?: string, properties?: Record<string, any>) {
    this.trackEvent({
      event_name: `journey_${step}`,
      event_category: 'user_action',
      user_id: userId,
      custom_parameters: properties
    });
  }

  // API call tracking
  trackAPICall(endpoint: string, method: string, duration: number, status: number, error?: string) {
    this.trackPerformance({
      event_name: 'api_response_time',
      endpoint: `${method} ${endpoint}`,
      duration_ms: duration,
      status_code: status,
      error_message: error
    });
  }
}

// Export singleton instance
export const analytics = new AnalyticsService();

// Helper functions for common tracking scenarios
export const trackPageView = (page: string, title?: string) => analytics.trackPageView(page, title);
export const trackEvent = (event: AnalyticsEvent) => analytics.trackEvent(event);
export const trackConversion = (event: ConversionEvent) => analytics.trackConversion(event);
export const trackError = (error: Error, context?: string, userId?: string) => analytics.trackError(error, context, userId);
export const setUser = (userId: string, properties?: Record<string, any>) => analytics.setUser(userId, properties);
export const trackRevenue = (amount: number, currency?: string, userId?: string, tier?: string) => analytics.trackRevenue(amount, currency, userId, tier);
export const trackUserJourney = (step: 'registration' | 'first_login' | 'first_task' | 'family_invite' | 'subscription_view' | 'subscription_start', userId?: string, properties?: Record<string, any>) => analytics.trackUserJourney(step, userId, properties);

export default analytics;